/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Form, Input, Modal, Popover } from 'antd';
import { MessageOutlined, PictureOutlined, SmileOutlined } from '@ant-design/icons';

// Misc imports
import { URL_REGEX } from '../../../../constants/regex';
import RichTextEditor from './RichTextEditor';
import VariableDropdown from './VariableDropdown';
import { useParams } from 'react-router-dom';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../../../constants/markdown';
import { VariableIcon } from '../../../Icons/VariableIcon';
import MediaWidgetModal from '../../../Modals/MediaWidgetModal';
import RichTextEditorEmojiPicker from './RichTextEditorEmojiPicker';
import { isEmptyObject } from '../../../../utils/is/isEmptyObject';
import { useLazyFetchStoryVariablesQuery } from '../../../../store/story/storyApi';

const QuillCustomToolbar = ({
  richEditorRef,
  quillRef,
  id = 'customToolbar',
  toolbarActions = Object.values(RICH_TEXT_TOOLBAR_ACTIONS),
  includedVariableTypes = [],
  botId, // To override the tenantId from the URL
  ...rest
}) => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();
  const { data: storyVariables } = getCurrentQueryState('fetchStoryVariables', { orgId, tenantId: botId || tenantId });
  const [fetchStoryVariables] = useLazyFetchStoryVariablesQuery();

  const [form] = Form.useForm();

  // For the image tool
  const [isMediaWidgetVisible, setIsMediaWidgetVisible] = useState(false);

  // For the tooltip tool
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [isTooltipModalOpen, setIsTooltipModalOpen] = useState(false);

  // For the link tool
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const [currentSelection, setCurrentSelection] = useState({});

  // For the variable tool
  const [isVariableDropdownVisible, setIsVariableDropdownVisible] = useState(false);
  const [variableDropdownPlacement, setVariableDropdownPlacement] = useState('bottomLeft');

  // For the emoji tool
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  // Fetch story variables if not already available
  useEffect(() => {
    if ((tenantId || botId) && !storyVariables) {
      fetchStoryVariables({ orgId, tenantId: botId || tenantId });
    }
  }, [storyVariables, orgId, tenantId, botId]);

  // Dismiss variable dropdown in case user clicks elsewhere
  useEffect(() => {
    const handleDropdownDismiss = (event) => {
      // dismiss if click is not performed on the dropdown menu
      if (isVariableDropdownVisible) {
        const dropdownElement = document.getElementById('variable-dropdown');

        if (dropdownElement && !dropdownElement.contains(event.target)) {
          setIsVariableDropdownVisible(false);
        }
      }
    };

    document.addEventListener('click', handleDropdownDismiss);

    return () => {
      document.removeEventListener('click', handleDropdownDismiss);
    };
  }, [isVariableDropdownVisible]);

  // Manually calculate the popover position for desired placement
  useEffect(() => {
    const triggerNode = document.querySelector('.ql-custom-variable');
    if (!triggerNode) {
      return;
    }

    const rect = triggerNode.getBoundingClientRect();
    const availableSpaceRight = window.innerWidth - rect.right;
    const availableSpaceBottom = window.innerHeight - rect.bottom;
    const availableSpaceTop = window.innerHeight - rect.top;

    const expectedPopoverWidth = 390; //The width of the VariableDropdown (fixed)
    const expectedPopoverHeight = 300; //The height of the VariableDropdown (max)
    let placement = 'bottomLeft';

    if (availableSpaceRight < expectedPopoverWidth) {
      placement = 'bottomRight';
    }

    if (availableSpaceBottom < expectedPopoverHeight) {
      if (availableSpaceTop >= expectedPopoverHeight) {
        placement = 'topRight';
      }
    }

    setVariableDropdownPlacement(placement);
  }, []);

  const handleEmojiButtonOnClick = useCallback(() => {
    setIsEmojiPickerOpen(!isEmojiPickerOpen);
  }, [isEmojiPickerOpen]);

  /**
   * Formats the tooltip text in the editor
   */
  const formatTooltipText = () => {
    if (richEditorRef) {
      const editor = quillRef;
      const { index, length } = editor.getSelection(true);
      const tooltip = tooltipMessage.replace(/"undefined"/g, '').trim();
      if (!tooltip) {
        return editor.formatText(index, length, { 'customLink': '' });
      }
      if (length) {
        return editor.formatText(index, length, {
          'customLink': {
            href: '##',
            title: tooltip
          }
        });
      } else {
        return editor.insertText(index, tooltip, {
          'customLink': {
            href: '##',
            title: tooltip
          }
        });
      }
    }
  };

  const handleEmojiSelection = (emoji) => {
    if (richEditorRef) {
      if (emoji) {
        const editor = quillRef;
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'emoji', emoji, 'user');
        editor.setSelection(range.index + emoji.length, 0);
      }
    }
    setIsEmojiPickerOpen(false);
  };

  /**
   * Formats the link text in the editor
   */
  const formatLinkText = () =>{
    if (richEditorRef) {
      form.validateFields()
        .then(formValues => {
          const link = encodeURI(formValues.link);
          const title = formValues.title;
          const editor = quillRef;
          let selectionIndex = null;
          let selectionLength = null;
          try {
            const { index, length } = editor.getSelection(true);
            selectionIndex = index;
            selectionLength = length;
          } catch (e){
            if(!isEmptyObject(currentSelection)) {
              selectionIndex = currentSelection.index;
              selectionLength = currentSelection.length;
            }

            if(!selectionIndex || !selectionLength) {
              console.log(e);
            }
          }

          if (!link) {
            return editor.formatText(selectionIndex, selectionLength, { 'customLink': '' });
          }
          if (selectionLength) {
            const value = editor.getText(selectionIndex, selectionLength);
            editor.deleteText(selectionIndex, selectionLength);
            return editor.insertText(selectionIndex, value, {
              'customLink': {
                href: link,
                title: title
              }
            });
          }

          return editor.insertText(selectionIndex, title, {
            'customLink': {
              href: link,
              title: title
            }
          });
        }).catch(errorInfo => errorInfo);
    }
  };

  /**
   * Inserts an image in the editor
   */
  const insertImageUrl = (imageURL) => {
    if (richEditorRef) {
      if (imageURL) {
        const editor = quillRef;
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'image', imageURL, 'user');
      }
    }
    setIsMediaWidgetVisible(false);
  };

  const closeMediaWidgetModal = (file) => {
    if (file?.url) {
      insertImageUrl(file.url);
    }
    if (isMediaWidgetVisible) {
      setIsMediaWidgetVisible(false);
    }
  };

  return (
    <div id={id} {...rest}>
      {
        toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.HEADING) ? (
          <select className="ql-header">
            <option value="1">{t('Heading 1')}</option>
            <option value="2">{t('Heading 2')}</option>
            <option value="3">{t('Heading 3')}</option>
            <option value="4">{t('Heading 4')}</option>
            <option value="5">{t('Heading 5')}</option>
            <option value="6">{t('Heading 6')}</option>
            <option value="" selected>{t('Normal')}</option>
          </select>
        ) : null
      }
      {
        toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.BOLD) ? (
          <button className="ql-bold"></button>
        ) : null
      }
      {
        toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.ITALIC) ? (
          <button className="ql-italic"></button>
        ) : null
      }

      {
        toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.EMOJI) ? (
          <Fragment>
            <button id="qlEmojiButton" className="ql-emoji" onClick={handleEmojiButtonOnClick}><SmileOutlined className="h-100"/></button>
            {
              isEmojiPickerOpen && (
                <RichTextEditorEmojiPicker
                  isOpen={isEmojiPickerOpen}
                  onEmojiSelect={handleEmojiSelection}
                  position={{
                    top: 3,
                    left: 2
                  }}
                  onClickOutside={() => {
                    setIsEmojiPickerOpen(false);
                  }}
                  toolbarButtonSelector="ql-emoji"
                  quill={richEditorRef}
                  containerSelector=".ql-container"
                />
              )
            }

          </Fragment>
        ) : null
      }

      {
        toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.ORDERED_LIST) ? (
          <button className="ql-list" value="ordered"></button>
        ) : null
      }

      {
        toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.BULLET_LIST) ? (
          <button className="ql-list" value="bullet"></button>
        ) : null
      }

      {
        toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.LINK) ? (
          <Popover
            title={t('Enter a link')}
            trigger="click"
            onClick={() => setIsLinkPopoverOpen(!isLinkPopoverOpen)}
            open={isLinkPopoverOpen}
            onOpenChange={(open) => {
              if (open) {
                const { index, length } = richEditorRef ? richEditorRef.getEditorSelection(true) : {};
                setCurrentSelection({ index, length });
                const checkForExistingLink = quillRef.getContents(index, length)?.ops;
                if (checkForExistingLink.length === 1 && checkForExistingLink[0]?.attributes?.customLink) {
                  const { href, title } = JSON.parse(checkForExistingLink[0].attributes.customLink);
                  if (href === '##') {
                    form.setFieldsValue({ 'link': '', 'title': '' });
                  } else {
                    form.setFieldsValue({ 'link': href, 'title': title ?? '' });
                  }
                } else {
                  form.setFieldsValue({ 'link': '', 'title': '' });
                }

                // We use the setTimeout in order to give time to react to render the link input
                setTimeout(() => {
                  form.getFieldInstance('link').focus();
                }, 0);

              } else {
                setIsLinkPopoverOpen(false);
                setCurrentSelection({});
              }
            }}
            content={
              <Form
                form={form}
                name="richTextLink"
                initialValues={{
                  link: '',
                  title: ''
                }}
              >
                <Form.Item
                  name="link"
                  rules={[{
                    validator: async (rule, value) => {
                      try {
                        if (!URL_REGEX.test(encodeURI(value)) && value) {
                          throw new Error(rule.message);
                        }
                      } catch {
                        throw new Error(rule.message);
                      }
                    },
                    message: `${t('Please enter a valid URL')}`
                  }]}
                >
                  <Input
                    placeholder="https://"
                  />
                </Form.Item>

                <Form.Item
                  name="title"
                >
                  <Input
                    placeholder={t('Enter a title')}
                  />
                </Form.Item>

                <Button
                  type="primary"
                  size="small"
                  htmlType="submit"
                  onClick={() => {
                    formatLinkText();
                    setIsLinkPopoverOpen(false);
                    setCurrentSelection({});
                  }}
                >
                  {t('Add')}
                </Button>
              </Form>
            }
          >
            <button className="ql-link"></button>
          </Popover>
        ) : null
      }

      {
        toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_IMAGE) ? (
          <Fragment>
            <button
              className="ql-custom-image"
              onClick={() => setIsMediaWidgetVisible(!isMediaWidgetVisible)}
            >
              <PictureOutlined className="h-100"/>
            </button>

            {
              isMediaWidgetVisible && (
                <MediaWidgetModal
                  title={t('Add Image')}
                  onSuccess={insertImageUrl}
                  onClose={closeMediaWidgetModal}
                  isModalOpen={isMediaWidgetVisible}
                />
              )
            }
          </Fragment>
        ) : null
      }

      {
        toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_TOOLTIP) ? (
          <Fragment>
            <button
              className="ql-custom-tooltip"
              onClick={() => {
                const range = richEditorRef?.getEditorSelection(true) ?? { index: 0, length: 0 };
                const { index, length } = range;
                const checkForExistingTooltip = quillRef.getContents(index, length)?.ops;
                if (checkForExistingTooltip.length === 1 && checkForExistingTooltip[0]?.attributes?.customLink) {
                  const { href, title } = JSON.parse(checkForExistingTooltip[0].attributes.customLink);
                  setTooltipMessage(href === '##' ? title : '');
                } else {
                  setTooltipMessage('');
                }
                setIsTooltipModalOpen(true);
              }}
            >
              <MessageOutlined className="h-100"/>
            </button>

            <Modal
              open={isTooltipModalOpen}
              title={t('Enter a tooltip')}
              onCancel={() => setIsTooltipModalOpen(false)}
              onOk={() => {
                formatTooltipText();
                setIsTooltipModalOpen(false);
              }}
              destroyOnClose
            >
              <RichTextEditor
                index="tooltip-rte"
                toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.BOLD, RICH_TEXT_TOOLBAR_ACTIONS.ITALIC, RICH_TEXT_TOOLBAR_ACTIONS.EMOJI]}
                placeholder={t('Enter a message')}
                value={tooltipMessage}
                onChange={setTooltipMessage}
              />
            </Modal>
          </Fragment>
        ) : null
      }

      {
        toolbarActions.includes(RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE) ? (
          <Popover
            trigger="click"
            // Set Table as parent container to fix layering issues with dropdown in LC Messages
            getPopupContainer={(triggerNode) => {
              if (triggerNode?.closest && triggerNode.closest('.ant-table-wrapper')) {
                return triggerNode.closest('.ant-table-wrapper');
              }
              return document.body;
            }}
            onClick={(event) => {
              event.stopPropagation(); //prevents weird ui glitches on open
              setIsVariableDropdownVisible(!isVariableDropdownVisible);
            }}
            open={isVariableDropdownVisible}
            destroyTooltipOnHide
            placement={variableDropdownPlacement}
            overlayClassName="p-0 variable-popover"
            content={
              <VariableDropdown
                bordered={false}
                storyVariables={storyVariables}
                rteEditor={quillRef}
                prefix="{{"
                suffix="}}"
                onAfterAdd={() => setIsVariableDropdownVisible(false)}
                onHideDropdown={() => setIsVariableDropdownVisible(false)}
                includedVariableTypes={includedVariableTypes}
              />
            }
          >
            <button className="ql-custom-variable">
              <VariableIcon className="h-100" style={{ marginTop: '-2px', paddingBottom: '2px' }} />
            </button>
          </Popover>
        ) : null
      }
    </div>
  );
};

QuillCustomToolbar.propTypes = {
  id: PropTypes.string,
  richEditorRef: PropTypes.object,
  quillRef: PropTypes.object,
  toolbarActions: PropTypes.array,
  includedVariableTypes: PropTypes.array,
  botId: PropTypes.string
};

export default QuillCustomToolbar;
